// CAPABILITY: Viewer chat shared config (frontend)
// Single source of truth for chat limits + option enums used across the
// dashboard settings form, the viewer composer, and client-side preview
// sanitization. The Cloud Functions keep a mirrored copy in
// functions/chatText.js — keep the two in sync.

export const CHAT_MODERATION_LEVELS = ["low", "medium", "high"];

export const DEFAULT_MAX_MESSAGE_CHARS = 500;
export const MIN_MAX_MESSAGE_CHARS = 100;
export const MAX_MAX_MESSAGE_CHARS = 1000;

// Counter turns warning at this fraction of the cap.
export const MESSAGE_WARN_RATIO = 0.9;

export const DISPLAY_NAME_MIN_CHARS = 3;
export const DISPLAY_NAME_MAX_CHARS = 24;

export const DEFAULT_SLOW_MODE_SECONDS = 3;

// Default chat settings stamped onto new pages / used when an event has no
// chat object yet.
export const createDefaultChatSettings = () => ({
  enabled: false,
  moderation_enabled: false,
  moderation_level: "medium",
  context_hint: "",
  max_message_chars: DEFAULT_MAX_MESSAGE_CHARS,
  slow_mode_seconds: DEFAULT_SLOW_MODE_SECONDS,
});

// Normalize an arbitrary stored chat object into a complete, safe settings shape.
export const normalizeChatSettings = (chat) => {
  const defaults = createDefaultChatSettings();
  if (!chat || typeof chat !== "object") return defaults;

  // Migrate the old two-level scheme: old "strict" -> "high".
  const rawLevel = chat.moderation_level === "strict" ? "high" : chat.moderation_level;
  const level = CHAT_MODERATION_LEVELS.includes(rawLevel) ? rawLevel : defaults.moderation_level;

  const maxChars = Number(chat.max_message_chars);
  const clampedMax = Number.isFinite(maxChars)
    ? Math.min(MAX_MAX_MESSAGE_CHARS, Math.max(MIN_MAX_MESSAGE_CHARS, Math.round(maxChars)))
    : defaults.max_message_chars;

  const slow = Number(chat.slow_mode_seconds);
  const clampedSlow = Number.isFinite(slow) ? Math.min(60, Math.max(0, Math.round(slow))) : defaults.slow_mode_seconds;

  return {
    enabled: Boolean(chat.enabled),
    moderation_enabled: Boolean(chat.enabled) && Boolean(chat.moderation_enabled),
    moderation_level: level,
    context_hint: typeof chat.context_hint === "string" ? chat.context_hint.slice(0, 280) : "",
    max_message_chars: clampedMax,
    slow_mode_seconds: clampedSlow,
  };
};
